<script lang="ts">
	import { goto } from '$app/navigation';
	import Svg from '$lib/svg/svg.svelte';
	import type { Icon } from '$lib/types/icons';
	import {
		classesDependingOnProfile,
		getRoundedClasses,
		type WidgetButtonStyle
	} from '$lib/widget/helper/widget';
	import { widgetConfig } from '$lib/widget/stores';
	import { fade } from 'svelte/transition';

	interface Props {
		className?: string;
		shake?: boolean;
		zoom?: boolean;
		transparentBackground?: boolean;
		textClasses?: string;
		useOutline?: boolean;
		height?: string;
		width?: string;
		padding?: string;
		href?: string;
		disabled?: boolean;
		selected?: boolean;
		dataTestId?: string;
		showIcon?: boolean;
		title?: string;
		icon?: Icon;
		iconPosition?: 'left' | 'right';
		iconClasses?: string;
		iconSizeClasses?: string;
		overrideCursor?: boolean;
		scaleOnHover?: boolean;
		align?: string;
		element?: HTMLDivElement | undefined;
		backgroundImage?: string;
		rounded?: 'auto' | WidgetButtonStyle;
		onclick?: () => void;
		children?: import('svelte').Snippet;
	}

	let {
		className = '',
		shake = false,
		zoom = false,
		transparentBackground = false,
		textClasses = 'text-widget-primary-on dark:text-widget-primary-dark-on',
		useOutline = true,
		height = '',
		width = '',
		padding = 'p-2',
		href = '',
		disabled = false,
		selected = false,
		dataTestId = '',
		showIcon = true,
		title = '',
		icon = '',
		iconPosition = 'left',
		iconClasses = '',
		iconSizeClasses = 'w-5 h-5',
		overrideCursor = false,
		scaleOnHover = false,
		align = 'justify-center',
		element = $bindable(undefined),
		backgroundImage = '',
		rounded = 'auto',
		onclick = () => {},
		children
	}: Props = $props();

	let style = '';
	function click() {
		if (href != '') {
			goto(href);
		}
		onclick();
	}

	function getBackgroundClasses(selected: boolean) {
		let classes = [];
		if (!transparentBackground) {
			classes.push('disabled:bg-widget-primary/20 disabled:dark:bg-widget-primary-dark/20 ');
			classes.push('bg-widget-primary dark:bg-widget-primary-dark');

			if (selected) {
				classes.push(
					classesDependingOnProfile(
						'brightness-125 contrast-100',
						'dark:brightness-125 dark:contrast-100'
					)
				);
			}
		}

		return classes.join(' ');
	}

	function getOutlineClasses(selected: boolean) {
		let classes = ['outline-none'];
		if (useOutline) {
			classes.push('hover:outline-widget-primary hover:dark:outline-widget-primary-dark');
			classes.push('disabled:outline-transparent disabled:dark:outline-transparent');

			if (selected) {
				classes.push('outline-widget-primary/60 dark:outline-widget-primary-dark/60');
			}
		}

		return classes.join(' ');
	}

	function getTextClasses() {
		let classes = ['disabled:text-opacity-50'];
		if (transparentBackground) {
			classes.push('hover:text-widget-primary dark:hover:text-widget-primary-dark');
		}

		if (backgroundImage) {
			classes = ['text-[#FFFFFF]', ...classes, 'text-lg font-medium'];
		} else {
			classes = [textClasses, ...classes];
		}

		return classes.join(' ');
	}

	// Do not apply rounded classes if there is a background image
	let buttonRoundedClasses = $derived(
		backgroundImage ? 'rounded-md' : getRoundedClasses($widgetConfig.theme.buttonStyle, rounded)
	);
</script>

<div bind:this={element} class="h-full flex {width}">
	<button
		in:fade={{ duration: 200 }}
		class="relative flex-none transition-all duration-300

        {buttonRoundedClasses}
        {getBackgroundClasses(selected)}
        {getOutlineClasses(selected)}
        {getTextClasses()}
        {scaleOnHover ? 'hover:scale-[1.02] hover:z-10' : ''}

        {padding}
        flex {align} gap-2
        {backgroundImage ? 'items-end' : 'items-center'}
        flex-nowrap text-nowrap truncate
        {className}
        {height}
        {shake ? 'shake-animation' : ''}
        {zoom ? 'zoom-animation' : ''}
        {overrideCursor ? 'cursor-default' : ''}
      "
		{disabled}
		class:cursor-not-allowed={disabled}
		data-testid={dataTestId}
		id={dataTestId}
		type="button"
		{title}
		{style}
		onclickcapture={(e) => {
			e.stopPropagation();
			click();
		}}
	>
		{#if backgroundImage}
			<div class="absolute top-0 inset-0 bg-black">
				<img src={backgroundImage} alt={title} class="object-cover w-full h-full" />
			</div>
			<div
				class="absolute top-0 bg-gradient-to-t from-[rgba(0,0,0,0.8)] to-[rgba(0,0,0,0.1)] bg-opacity-50 w-full h-full"
			></div>
		{/if}
		<span
			class="relative flex items-center {align} gap-2 text-wrap {iconPosition === 'right'
				? 'flex-row-reverse'
				: ''}"
		>
			{#if showIcon}
				{#if icon}
					<Svg src={icon} className="{iconSizeClasses} shrink-0 {iconClasses}" />
				{/if}
			{/if}

			{@render children?.()}
		</span>
	</button>
</div>

<style>
	@keyframes shake {
		0% {
			transform: translateX(0);
		}
		25% {
			transform: translateX(-5px);
		}
		50% {
			transform: translateX(5px);
		}
		75% {
			transform: translateX(-5px);
		}
		100% {
			transform: translateX(0);
		}
	}

	.shake-animation {
		animation: shake 0.1s linear 5; /* 0.1s * 20 itérations = 2 secondes */
	}

	@keyframes zoom {
		0% {
			transform: scale(0);
			opacity: 0;
		}
		25% {
			transform: scale(1.5);
			opacity: 1;
		}
		50% {
			transform: scale(1);
			opacity: 1;
		}
		75% {
			transform: scale(1.2);
			opacity: 1;
		}
		100% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.zoom-animation {
		animation: zoom 0.4s linear 1;
	}
</style>
